/**
 * React Query cache key factories for the Auth API.
 * Tuple shapes match existing inline keys for invalidation compatibility.
 */

import type { ClientsListQueryParams } from './interfaces';

type UsersWithTenantsFilters = {
  page: number;
  limit: number;
  companyId?: string;
  clientId?: string;
  role?: string;
  search?: string;
};

/** Cache keys for auth queries and mutations. */
export const authKeys = {
  /** `['profile']` */
  profile: () => ['profile'] as const,
  /** `['tenant-memberships']` */
  tenantMemberships: () => ['tenant-memberships'] as const,
  /** `['users-list']` */
  usersList: () => ['users-list'] as const,
  /** Prefix for invalidating all client list queries: `['clients-with-companies']` */
  clientsWithCompaniesAll: () => ['clients-with-companies'] as const,
  /** `['clients-with-companies', params]` */
  clientsWithCompanies: (params: ClientsListQueryParams) =>
    ['clients-with-companies', params] as const,
  /** Prefix for invalidating all user-tenant list queries: `['users-with-tenants']` */
  usersWithTenantsAll: () => ['users-with-tenants'] as const,
  /** `['users-with-tenants', filters]` */
  usersWithTenants: (filters: UsersWithTenantsFilters) =>
    ['users-with-tenants', filters] as const,
  /** `['client', clientId]` */
  client: (clientId: string) => ['client', clientId] as const,
  weeklySummarySettings: (clientId: string) => ['weekly-summary-settings', clientId] as const,
  /** `['company', companyId]` */
  company: (companyId: string) => ['company', companyId] as const,
  /** `['user-tenants-by-user-id', userId]` */
  userTenantsByUserId: (userId: string) => ['user-tenants-by-user-id', userId] as const,
  /** `['client-employees', clientId]` */
  clientEmployees: (clientId: string) => ['client-employees', clientId] as const,
  /** `['client-owners', clientId]` */
  clientOwners: (clientId: string) => ['client-owners', clientId] as const,
  /** Prefix for invalidating every client's customers list: `['client-customers']` */
  clientCustomersAll: () => ['client-customers'] as const,
  /** `['client-customers', clientId]` */
  clientCustomers: (clientId: string) => ['client-customers', clientId] as const,
};
